import { useEffect, useState, type FormEvent } from "react";

import Modal from "../../components/Modal";
import { ApiError } from "../../api/client";
import { useCreateFuelStation } from "../../api/fuelStations";
import type {
  CountryOption,
  FuelStation,
  CreateFuelStationInput,
} from "../../api/types";

interface FuelStationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  countries: CountryOption[];
  defaultCountry: string;
  onCreated?: (station: FuelStation) => void;
}

export default function FuelStationFormModal({
  isOpen,
  onClose,
  countries,
  defaultCountry,
  onCreated,
}: FuelStationFormModalProps) {
  const create = useCreateFuelStation();
  const [values, setValues] = useState<CreateFuelStationInput>({
    name: "",
    city: "",
    country: defaultCountry,
    street: "",
    street_number: "",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setValues({
      name: "",
      city: "",
      country: defaultCountry,
      street: "",
      street_number: "",
      notes: "",
    });
    setError(null);
  }, [defaultCountry, isOpen]);

  const setField = (field: keyof CreateFuelStationInput, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      const result = await create.mutateAsync({
        name: values.name.trim(),
        city: values.city.trim(),
        country: values.country,
        street: values.street?.trim() || undefined,
        street_number: values.street_number?.trim() || undefined,
        notes: values.notes?.trim() || undefined,
      });
      onCreated?.(result.station);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add station.");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add fuel station">
      <form className="entry-form" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="field__input"
            type="text"
            required
            maxLength={255}
            value={values.name}
            onChange={(e) => setField("name", e.target.value)}
          />
        </label>

        <div className="field-group">
          <div className="field-row field-row--location">
            <label className="field field--grow">
              <span className="field__label">City</span>
              <input
                className="field__input"
                type="text"
                required
                maxLength={255}
                value={values.city}
                onChange={(e) => setField("city", e.target.value)}
              />
            </label>
            <label className="field field--country">
              <span className="field__label">Country</span>
              <select
                className="field__input field__select"
                required
                value={values.country}
                onChange={(e) => setField("country", e.target.value)}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} [{c.code}]
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field-row">
            <label className="field field--grow">
              <span className="field__label">Street</span>
              <input
                className="field__input"
                type="text"
                maxLength={255}
                value={values.street ?? ""}
                onChange={(e) => setField("street", e.target.value)}
              />
            </label>
            <label className="field field--number">
              <span className="field__label">Number</span>
              <input
                className="field__input"
                type="text"
                maxLength={64}
                value={values.street_number ?? ""}
                onChange={(e) => setField("street_number", e.target.value)}
              />
            </label>
          </div>
        </div>

        <label className="field">
          <span className="field__label">Notes (optional)</span>
          <textarea
            className="field__input field__textarea"
            maxLength={1024}
            value={values.notes ?? ""}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </label>

        {error && (
          <p className="form-status" data-variant="error" role="alert">
            {error}
          </p>
        )}

        <div className="form-actions form-actions--inline">
          <button
            type="button"
            className="button"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={create.isPending || !values.name.trim() || !values.city.trim()}
          >
            {create.isPending ? "Saving..." : "Add station"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
